import { useState } from 'react';
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  IconButton,
  Tooltip
} from '@mui/material';
import PowerSettingsNewIcon from '@mui/icons-material/PowerSettingsNew';
import { JobProgressStepper } from 'src/Common/components/JobProgressStepper/JobProgressStepper';
import { useSystemKillSwitch } from 'src/Common/hooks/useSystemKillSwitch';

/**
 * Master power toggle for the prediction side — one global-nav button that
 * stops OR starts Opportunities + the unwinder + the opener in a single job,
 * with live step-by-step progress in the dialog. Direction is driven by the
 * aggregate running state: anything up → "Stop all"; everything down →
 * "Start all". Positions and notifications (TNA alerts) are always-on and
 * unaffected either way.
 */
export const StopAllButton = () => {
  const { isRunning, currentJob, stopAll, startAll, isStopping, isStarting } =
    useSystemKillSwitch();
  const [open, setOpen] = useState(false);
  const [confirmed, setConfirmed] = useState(false);

  // Anything running → the button stops; everything down → it starts.
  const action: 'start' | 'stop' = isRunning ? 'stop' : 'start';

  // Only the master 'all' job is ours — ignore per-subsystem jobs.
  const myJob =
    confirmed && currentJob?.subsystem === 'all' ? currentJob : null;
  const jobRunning =
    myJob?.status === 'running' || isStopping || isStarting;

  // The backend rejects start/stop-all while another on/off job is in flight
  // and returns that job instead — surface it rather than spinning forever.
  const busyWithOther = Boolean(
    confirmed &&
      !isStopping &&
      !isStarting &&
      currentJob &&
      currentJob.subsystem !== 'all' &&
      currentJob.status === 'running'
  );

  const handleClose = () => {
    if (jobRunning) return; // don't let them dismiss mid-run
    setOpen(false);
    setConfirmed(false);
  };

  const handleConfirm = () => {
    setConfirmed(true);
    if (action === 'stop') stopAll();
    else startAll();
  };

  return (
    <>
      <Tooltip
        title={
          action === 'stop'
            ? 'Stop ALL prediction systems'
            : 'Start ALL prediction systems'
        }
        placement="right"
      >
        <IconButton
          onClick={() => setOpen(true)}
          size="small"
          sx={{
            color: theme =>
              action === 'stop'
                ? theme.palette.error.main
                : theme.palette.success.main,
            borderRadius: '5px',
            '& > svg': { fontSize: '1.1rem' }
          }}
        >
          <PowerSettingsNewIcon />
        </IconButton>
      </Tooltip>

      <Dialog open={open} onClose={handleClose} maxWidth="xs" fullWidth>
        <DialogTitle>
          {action === 'stop'
            ? 'Stop all prediction systems'
            : 'Start all prediction systems'}
        </DialogTitle>
        <DialogContent>
          {!confirmed ? (
            <DialogContentText component="div">
              {action === 'stop' ? (
                <>
                  This stops <b>Opportunities</b>, the <b>unwinder</b> and the{' '}
                  <b>opener</b> — scaling their pods and node groups to zero.
                  <br />
                  <br />
                  Positions and notifications stay running, so TNA alerts keep
                  flowing.
                </>
              ) : (
                <>
                  This starts <b>Opportunities</b>, the <b>unwinder</b> and the{' '}
                  <b>opener</b> — scaling the node groups and pods back up.
                  Takes a few minutes.
                </>
              )}
            </DialogContentText>
          ) : myJob ? (
            <JobProgressStepper job={myJob} label="Everything" />
          ) : busyWithOther ? (
            <DialogContentText>
              Another on/off operation is in progress. Wait for it to finish,
              then try again.
            </DialogContentText>
          ) : (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
              <CircularProgress size={28} />
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          {!confirmed ? (
            <>
              <Button onClick={handleClose} color="inherit">
                Cancel
              </Button>
              <Button
                onClick={handleConfirm}
                color={action === 'stop' ? 'error' : 'success'}
                variant="contained"
                startIcon={<PowerSettingsNewIcon />}
              >
                {action === 'stop' ? 'Stop Everything' : 'Start Everything'}
              </Button>
            </>
          ) : (
            <Button onClick={handleClose} disabled={jobRunning}>
              {jobRunning
                ? action === 'stop'
                  ? 'Stopping…'
                  : 'Starting…'
                : 'Close'}
            </Button>
          )}
        </DialogActions>
      </Dialog>
    </>
  );
};
